import { useState, useEffect } from 'react'
import { Eye, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { api } from '@/services/api'
import {
  ProjectVisibility,
  ProjectVisibilityLabels,
  enumToSelectOptions,
} from '@/utils/enum'

export interface ClientViewConfigFormData {
  tasksVisibleClient: boolean
  roadmapVisibleClient: boolean
  metricsVisibleClient: boolean
  visibility: string
}

export interface ClientViewConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  projectId: string
  isSuperAdmin?: boolean
  initialValues: {
    tasks_visible_client?: boolean
    roadmap_visible_client?: boolean
    metrics_visible_client?: boolean
    visibility?: string
  }
}

function VisibilityToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-1 min-w-0 pr-3">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-cyan-500' : 'bg-gray-300'
        }`}
        aria-pressed={checked}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

export function ClientViewConfigModal({
  open,
  onOpenChange,
  onSuccess,
  projectId,
  isSuperAdmin = false,
  initialValues,
}: ClientViewConfigModalProps) {
  const [form, setForm] = useState<ClientViewConfigFormData>({
    tasksVisibleClient: true,
    roadmapVisibleClient: true,
    metricsVisibleClient: true,
    visibility: ProjectVisibility.EXTERNE,
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setForm({
        tasksVisibleClient: initialValues.tasks_visible_client ?? true,
        roadmapVisibleClient: initialValues.roadmap_visible_client ?? true,
        metricsVisibleClient: initialValues.metrics_visible_client ?? true,
        visibility: initialValues.visibility || ProjectVisibility.EXTERNE,
      })
    }
  }, [
    open,
    initialValues.tasks_visible_client,
    initialValues.roadmap_visible_client,
    initialValues.metrics_visible_client,
    initialValues.visibility,
  ])

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const payload: Record<string, boolean | string> = {
        tasks_visible_client: form.tasksVisibleClient,
        roadmap_visible_client: form.roadmapVisibleClient,
        metrics_visible_client: form.metricsVisibleClient,
      }
      if (isSuperAdmin) {
        payload.visibility = form.visibility
      }
      await api.put(`/projects/${projectId}`, payload)
      onSuccess()
      onOpenChange(false)
    } catch (error: unknown) {
      console.error('Erreur lors de la mise à jour de la configuration vue client:', error)
      const err = error as { response?: { detail?: string } }
      alert(err?.response?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-cyan-500" />
            <h2 className="text-2xl font-bold text-gray-800">Configuration vue client</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Choisissez ce que le client voit sur la page du projet
          </p>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Sections visibles</p>
          <VisibilityToggle
            label="Section Tâches"
            description="Afficher le tableau des tâches à effectuer et réalisées"
            checked={form.tasksVisibleClient}
            onChange={(value) => setForm((prev) => ({ ...prev, tasksVisibleClient: value }))}
          />
          <VisibilityToggle
            label="Section Métriques"
            description="Afficher les métriques et graphiques de suivi"
            checked={form.metricsVisibleClient}
            onChange={(value) => setForm((prev) => ({ ...prev, metricsVisibleClient: value }))}
          />
          <VisibilityToggle
            label="Section Roadmap"
            description="Afficher la feuille de route du projet"
            checked={form.roadmapVisibleClient}
            onChange={(value) => setForm((prev) => ({ ...prev, roadmapVisibleClient: value }))}
          />
        </div>

        {isSuperAdmin && (
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-500" />
              <p className="text-sm font-semibold text-gray-700">Type de projet</p>
            </div>
            <p className="text-xs text-gray-500">
              Les projets internes sont exclus des analytics globales
            </p>
            <Select
              value={form.visibility}
              onChange={(e) => setForm((prev) => ({ ...prev, visibility: e.target.value }))}
              options={enumToSelectOptions(ProjectVisibility, ProjectVisibilityLabels)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </div>
        )}

      </div>
    </Modal>
  )
}
